import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, KeyRound, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { useUsers, useUpdateUser, useDeleteUser } from "@/hooks/use-users";
import { toast } from "sonner";
import type { User } from "@/types";

export function UsersPage() {
  const { data: users, isLoading } = useUsers();
  const updateUser = useUpdateUser();
  const deleteUser = useDeleteUser();

  const handleToggleEnabled = async (user: User, checked: boolean) => {
    try {
      await updateUser.mutateAsync({ uid: user.uid, data: { isEnabled: checked } });
      toast.success(`${user.uid} ${checked ? "enabled" : "disabled"}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to update user");
    }
  };

  const handleDelete = async (user: User) => {
    try {
      await deleteUser.mutateAsync(user.uid);
      toast.success(`${user.uid} deleted`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to delete user");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Users</h1>
          <p className="text-sm text-muted-foreground">
            Manage VPN user accounts stored in OpenLDAP.
          </p>
        </div>
        <Button asChild className="w-full sm:w-auto h-11">
          <Link to="/users/create">
            <Plus className="mr-2 h-4 w-4" />
            Create user
          </Link>
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      ) : !users || users.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <p className="text-4xl mb-3">&#x1F464;</p>
            <p className="text-muted-foreground">No users found.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Mobile card list */}
          <div className="grid gap-3 md:hidden">
            {users.map((user) => (
              <Card key={user.uid}>
                <CardContent className="p-4">
                  <div className="mb-3 flex items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold">{user.uid}</p>
                    </div>
                    <div className="flex gap-1">
                      {user.isAdmin && <Badge variant="secondary">Admin</Badge>}
                      <Badge variant={user.isEnabled ? "default" : "outline"}>
                        {user.isEnabled ? "Enabled" : "Disabled"}
                      </Badge>
                    </div>
                  </div>

                  <div className="mb-4 flex items-center gap-2 text-sm">
                    <span className="text-muted-foreground">Active:</span>
                    <Switch
                      checked={user.isEnabled}
                      onCheckedChange={(checked) => handleToggleEnabled(user, checked)}
                      disabled={updateUser.isPending}
                      aria-label={`Toggle ${user.uid}`}
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <Button variant="outline" size="sm" asChild className="w-full">
                      <Link to={`/users/${encodeURIComponent(user.uid)}/edit`}>
                        <Pencil className="mr-2 h-4 w-4" /> Edit
                      </Link>
                    </Button>
                    <Button variant="outline" size="sm" asChild className="w-full">
                      <Link to={`/users/${encodeURIComponent(user.uid)}/password`}>
                        <KeyRound className="mr-2 h-4 w-4" /> Password
                      </Link>
                    </Button>
                    <DeleteButton user={user} onConfirm={() => handleDelete(user)} isPending={deleteUser.isPending} />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Desktop table */}
          <div className="hidden rounded-lg border bg-card md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Username</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Admin</TableHead>
                  <TableHead className="w-[200px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.uid}>
                    <TableCell className="font-medium">{user.uid}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={user.isEnabled}
                          onCheckedChange={(checked) => handleToggleEnabled(user, checked)}
                          disabled={updateUser.isPending}
                          aria-label={`Toggle ${user.uid}`}
                        />
                        <Badge variant={user.isEnabled ? "default" : "outline"}>
                          {user.isEnabled ? "Enabled" : "Disabled"}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell>{user.isAdmin && <Badge variant="secondary">Admin</Badge>}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="icon" asChild>
                          <Link to={`/users/${encodeURIComponent(user.uid)}/edit`}>
                            <Pencil className="h-4 w-4" />
                          </Link>
                        </Button>
                        <Button variant="ghost" size="icon" asChild>
                          <Link to={`/users/${encodeURIComponent(user.uid)}/password`}>
                            <KeyRound className="h-4 w-4" />
                          </Link>
                        </Button>
                        <DeleteButton
                          user={user}
                          onConfirm={() => handleDelete(user)}
                          isPending={deleteUser.isPending}
                          variant="ghost"
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
}

function DeleteButton({
  user,
  onConfirm,
  isPending,
  variant = "outline",
}: {
  user: User;
  onConfirm: () => void;
  isPending: boolean;
  variant?: "outline" | "ghost";
}) {
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant={variant} size={variant === "ghost" ? "icon" : "sm"} className={variant === "outline" ? "w-full" : ""}>
          {variant === "outline" ? (
            <>
              <Trash2 className="mr-2 h-4 w-4" /> Delete
            </>
          ) : (
            <Trash2 className="h-4 w-4 text-destructive" />
          )}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete user</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete <strong>{user.uid}</strong>? This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-col gap-2 sm:flex-row">
          <Button variant="outline" onClick={() => setOpen(false)} className="w-full sm:w-auto">
            Cancel
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={isPending} className="w-full sm:w-auto">
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
